import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, X, PieChart, ClipboardList, Loader2, DollarSign } from 'lucide-react'
import { useHomeStore } from './store'
import { useAuthStore } from '@store/authStore'
import { preferencesApi, draftsApi, dealsApi } from '@api/client'
import { showToast } from '@store/uiStore'
import { HubSpotSearch } from './components/HubSpotSearch'
import { SessionsTable } from './components/SessionsTable'
import { ReviewQueueTab } from './components/ReviewQueueTab'
import { ApprovedTab } from './components/ApprovedTab'
import { DuplicateSessionModal } from './components/DuplicateSessionModal'
import type { DealHubSession } from './types'
import type { Draft } from '@api/types'

export function HomePage() {
  const { selectedDeal, sessions, isLoadingSessions, recentSessions, loadRecentSessions, createSession, pagination } = useHomeStore()
  const { user } = useAuthStore()
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false)
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [showScopePrompt, setShowScopePrompt] = useState(false)
  const [scopeCustomerName, setScopeCustomerName] = useState('')
  const [creatingScopeDraft, setCreatingScopeDraft] = useState(false)
  const [homeTab, setHomeTab] = useState<'recent' | 'review' | 'approved'>('recent')
  const navigate = useNavigate()

  useEffect(() => {
    loadRecentSessions()
  }, [loadRecentSessions])

  // Load drafts for current user
  useEffect(() => {
    if (!user?.email) return
    const loadDrafts = async () => {
      try {
        const res = await draftsApi.list({ user_email: user.email })
        if (res.success && res.data) {
          setDrafts(res.data)
        }
      } catch (error) {
        console.error('Failed to load drafts:', error)
      }
    }
    loadDrafts()
  }, [user?.email])

  const handleDraftDeleted = useCallback((draftId: number) => {
    setDrafts((prev) => prev.filter((d) => d.id !== draftId))
  }, [])

  // Check if user has completed onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.email) return
      try {
        const response = await preferencesApi.get(user.email)
        const acknowledged = response.data?.preferences?.onboarding_acknowledged || []
        // Show banner if user hasn't acknowledged all 6 modules
        if (acknowledged.length < 6) {
          setShowOnboardingBanner(true)
        }
      } catch {
        // Silently fail — don't block the page
      }
    }
    checkOnboarding()
  }, [user?.email])

  const handleCreateScopeDraft = async () => {
    const name = scopeCustomerName.trim()
    if (!name) {
      showToast('Please enter a customer name', 'warning')
      return
    }
    setCreatingScopeDraft(true)
    try {
      const res = await dealsApi.create({
        customer_name: name,
        created_by: user?.email || undefined,
        created_by_name: user?.name || undefined,
      })
      if (!res.success || !res.data) {
        showToast(res.error || 'Failed to create scope draft', 'error')
        return
      }
      setShowScopePrompt(false)
      setScopeCustomerName('')
      navigate(`/scope?session=${res.data.id}`)
    } catch {
      showToast('Failed to create scope draft', 'error')
    } finally {
      setCreatingScopeDraft(false)
    }
  }

  const handleCreateSession = async () => {
    if (!selectedDeal) return

    // If sessions exist for this deal, show the warning modal
    if (sessions.length > 0) {
      setShowDuplicateModal(true)
      return
    }

    // No existing sessions, create directly
    await createSession(selectedDeal)
  }

  const handleConfirmCreate = async () => {
    if (selectedDeal) {
      setShowDuplicateModal(false)
      await createSession(selectedDeal)
    }
  }

  const handleUseExisting = (session: DealHubSession) => {
    setShowDuplicateModal(false)
    // Navigate to the first available module, preferring Quote > Scope > ROI
    if (session.hasQuote) {
      navigate(`/quote?session=${session.id}`)
    } else if (session.hasScope) {
      navigate(`/scope?session=${session.id}`)
    } else if (session.hasROI) {
      navigate(`/roi?session=${session.id}`)
    } else {
      // No modules yet, default to quote
      navigate(`/quote?session=${session.id}`)
    }
  }

  return (
    <div className="space-y-6">
      {/* Onboarding Banner */}
      {showOnboardingBanner && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen size={20} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">New to DealHub?</p>
              <p className="text-xs text-[var(--text-muted)]">
                Learn how each module works in Settings &rarr; How It Works.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/settings/how-it-works')}
              className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              Take a tour
            </button>
            <button
              onClick={() => setShowOnboardingBanner(false)}
              className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Home</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">Search HubSpot deals and manage DealHub sessions</p>
      </div>

      {/* CTA Split: Select a Deal / Draft Mode */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left: Select a Deal */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-[var(--text-primary)]">Select a Deal</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">Search for a HubSpot deal to view or create sessions</p>
          </div>
          <HubSpotSearch />
        </div>

        {/* Right: Draft Mode */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6 flex flex-col">
          <div className="mb-4">
            <h2 className="text-lg font-medium text-[var(--text-primary)]">Draft Mode</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">Start working without a deal — your work is auto-saved and you can link a deal later</p>
          </div>
          <div className="mt-auto flex flex-col gap-3">
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/quote?draft=new')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 border border-primary/30 rounded-lg text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
              >
                <DollarSign size={16} />
                New Quote Draft
              </button>
              <button
                onClick={() => navigate('/roi?draft=new')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm font-medium text-emerald-500 hover:bg-emerald-500/20 transition-colors"
              >
                <PieChart size={16} />
                New ROI Draft
              </button>
            </div>
            {showScopePrompt ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 h-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-amber-500"
                  placeholder="Customer name..."
                  value={scopeCustomerName}
                  onChange={(e) => setScopeCustomerName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateScopeDraft()}
                  autoFocus
                />
                <button
                  onClick={handleCreateScopeDraft}
                  disabled={creatingScopeDraft}
                  className="px-4 h-10 bg-amber-500/20 border border-amber-500/30 rounded-lg text-sm font-medium text-amber-500 hover:bg-amber-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {creatingScopeDraft ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />}
                  Create
                </button>
                <button
                  onClick={() => { setShowScopePrompt(false); setScopeCustomerName('') }}
                  className="px-2 h-10 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowScopePrompt(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm font-medium text-amber-500 hover:bg-amber-500/20 transition-colors"
              >
                <ClipboardList size={16} />
                New Scope Draft
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sessions for Selected Deal */}
      {selectedDeal && (
        <SessionsTable
          sessions={sessions}
          isLoading={isLoadingSessions}
          title={`Sessions for ${selectedDeal.name}`}
          emptyMessage="No sessions found for this deal. Create one to get started."
          showDealName={false}
          onCreateSession={sessions.length === 0 ? handleCreateSession : undefined}
        />
      )}

      {/* Recent Sessions & Drafts / In Review (when no deal selected) */}
      {!selectedDeal && (
        <div>
          {/* Tabs */}
          <div className="mb-4 flex items-center gap-1 border-b border-[var(--border-color)]">
            {([
              { key: 'recent', label: 'Recent Sessions & Drafts' },
              { key: 'review', label: 'In Review' },
              { key: 'approved', label: 'Approved' },
            ] as const).map((t) => (
              <button
                key={t.key}
                onClick={() => setHomeTab(t.key)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  homeTab === t.key
                    ? 'border-primary text-[var(--text-primary)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {homeTab === 'recent' ? (
            <SessionsTable
              sessions={recentSessions}
              drafts={drafts}
              title="All Recent Sessions & Drafts"
              emptyMessage="No sessions or drafts yet. Search for a deal above or start a draft to get going."
              showDealName={true}
              pagination={pagination}
              onPageChange={(page) => loadRecentSessions(page)}
              onDraftDeleted={handleDraftDeleted}
            />
          ) : homeTab === 'review' ? (
            <ReviewQueueTab />
          ) : (
            <ApprovedTab />
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
          <div className="text-2xl font-bold text-[var(--text-primary)]">{pagination.totalSessions}</div>
          <div className="text-sm text-[var(--text-muted)]">Total Sessions</div>
        </div>
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
          <div className="text-2xl font-bold text-[var(--text-primary)]">
            {recentSessions.filter((s) => s.hasQuote).length}
          </div>
          <div className="text-sm text-[var(--text-muted)]">Sessions with Quotes</div>
        </div>
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
          <div className="text-2xl font-bold text-[var(--text-primary)]">
            {recentSessions.filter((s) => s.hasScope).length}
          </div>
          <div className="text-sm text-[var(--text-muted)]">Sessions with Scope</div>
        </div>
      </div>

      {/* Duplicate Session Warning Modal */}
      <DuplicateSessionModal
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onConfirm={handleConfirmCreate}
        onUseExisting={handleUseExisting}
        sessions={sessions}
        dealName={selectedDeal?.name || ''}
      />
    </div>
  )
}
